import { useCallback } from 'react';

import { MutationResult, useMutation } from '@apollo/client';
import { ExecutionResult } from 'graphql';

import {
  FitnessApplicationSettingsInput,
  PageFitnessSettingsQuery,
  PageFitnessSettingsQueryVariables,
  PageType,
  UpdateFitnessSettingsMutation,
  UpdateFitnessSettingsMutationVariables,
} from '../../../types/__generated__';
import { useFitnessManagerContext } from '../../components/FitnessManager/FitnessManagerContext';
import PageFitnessSettings from '../../components/FitnessManager/PageFitnessSettings.gql';
import listTimelineEntriesQuery from '../timeline/queries/listTimelineEntries.gql';
import { usePageMeta } from '../usePageMeta';
import mutation from './queries/UpdateFitnessSettings.gql';

export type UpdateSettings = (
  input: FitnessApplicationSettingsInput,
) => Promise<ExecutionResult<UpdateFitnessSettingsMutation>>;

export default function useUpdateFitnessSettings(): [
  UpdateSettings,
  MutationResult<UpdateFitnessSettingsMutation>,
] {
  const { pageType, pageSlug } = usePageMeta();
  const { page, fitnessApplicationName } = useFitnessManagerContext();

  const [runMutation, mutationResult] = useMutation<
    UpdateFitnessSettingsMutation,
    UpdateFitnessSettingsMutationVariables
  >(mutation, {
    update(cache, { data: newData }) {
      if (!newData || !newData.setFitnessApplicationSettings) {
        return;
      }

      cache.updateQuery<
        PageFitnessSettingsQuery,
        PageFitnessSettingsQueryVariables
      >(
        {
          query: PageFitnessSettings,
          variables: {
            type: pageType.toUpperCase() as PageType,
            slug: pageSlug,
            application: fitnessApplicationName,
          },
        },
        data => {
          return {
            page: {
              ...data?.page,
              id: page.id,
              application: {
                __typename: 'FitnessApplication',
                ...data?.page?.application,
                settings: newData.setFitnessApplicationSettings,
              },
              totals: data?.page?.totals ?? null,
            },
          };
        },
      );
    },
    refetchQueries: result => {
      return [
        {
          query: listTimelineEntriesQuery,
          variables: {
            type: pageType.toUpperCase() as PageType,
            slug: pageSlug,
            measurementSystem:
              result.data?.setFitnessApplicationSettings?.measurementSystem,
          },
          context: {
            credentials: 'include',
          },
        },
      ];
    },
    context: {
      credentials: 'include',
    },
  });

  return [
    useCallback(
      input =>
        runMutation({
          variables: {
            input,
          },
        }),
      [runMutation],
    ),
    mutationResult,
  ];
}
